import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogPanel,
  DialogTitle,
  Transition,
  TransitionChild,
} from "@headlessui/react";
import { Fragment } from "react";
import {
  XMarkIcon,
  CheckCircleIcon,
  XCircleIcon,
  DocumentIcon,
  ArrowTopRightOnSquareIcon,
  BanknotesIcon,
  CreditCardIcon,
  ArrowPathIcon,
} from "@heroicons/react/24/outline";
import { Button } from "components/ui";
import clsx from "clsx";
import { toast } from "sonner";
import { VerifyOrRejectPayment } from "services/paymentServices";
import { JWT_HOST_API } from "configs/auth.config";
import { ConfirmModal } from "components/shared/ConfirmModal";
import { isUserAdmin } from "utils/permissionsUtils";

const getAttachmentUrl = (path) => {
  if (!path) return "";
  if (path.startsWith("http://") || path.startsWith("https://")) return path;
  const cleanPath = path.replace(/^\/+/, "");
  const cleanBase = JWT_HOST_API?.replace(/\/+$/, "");
  return `${cleanBase}/${cleanPath}`;
};

const PaymentVerifyModal = ({ isOpen, onClose, lead, onActionComplete, onRefundPayment, defaultReject = false }) => {
  const isAdmin = isUserAdmin();
  const [loading, setLoading] = useState(null); // 'approve' | 'reject' | null
  const [imageError, setImageError] = useState(false);
  const [isRejecting, setIsRejecting] = useState(false);
  const [remarks, setRemarks] = useState("");
  const [selectedAttachmentIndex, setSelectedAttachmentIndex] = useState(0);
  const [showRejectConfirm, setShowRejectConfirm] = useState(false);

  // Reset state when modal closes or lead changes
  useEffect(() => {
    if (isOpen) {
      if (defaultReject) {
        setIsRejecting(true);
      }
    } else {
      setIsRejecting(false);
      setRemarks("");
      setImageError(false);
      setLoading(null);
      setSelectedAttachmentIndex(0);
      setShowRejectConfirm(false);
    }
  }, [isOpen, defaultReject]);

  const attachments = React.useMemo(() => {
    if (!lead) return [];
    if (Array.isArray(lead.Attachments)) return lead.Attachments;
    if (typeof lead.Attachments === 'string') {
      try {
        return JSON.parse(lead.Attachments) || [];
      } catch (e) {
        console.error("Failed to parse attachments JSON string:", e);
      }
    }
    const legacyPath = lead.AttachmentPath || lead.AttachmentUrl || lead.Attachment;
    if (legacyPath) {
      return [{
        AttachmentId: lead.PaymentId || 'legacy',
        PaymentId: lead.PaymentId,
        FileName: legacyPath.substring(legacyPath.lastIndexOf('/') + 1) || 'Attachment',
        FilePath: legacyPath
      }];
    }
    return [];
  }, [lead]);

  if (!lead) return null;

  const isUPI = lead.PaymentMethod?.toLowerCase() === "upi";

  const verifyStatus = typeof lead.VerifyStatus !== 'undefined' ? lead.VerifyStatus : (typeof lead.verifyStatus !== 'undefined' ? lead.verifyStatus : null);
  const isVerified = verifyStatus === 1 || lead.IsVerified || lead.isVerified;
  const isRejected = verifyStatus === 2 || lead.IsRejected || lead.isRejected;
  const isRefunded = verifyStatus === 3 || lead.IsRefunded || lead.isRefunded;

  const currentAttachment = attachments[selectedAttachmentIndex];
  const attachmentUrl = getAttachmentUrl(currentAttachment?.FilePath);
  const isPdf = currentAttachment?.FileType?.toLowerCase() === 'application/pdf' || currentAttachment?.FilePath?.toLowerCase().endsWith('.pdf');

  const handleApprove = async () => {
    setLoading("approve");
    try {
      const result = await VerifyOrRejectPayment({
        paymentId: lead.PaymentId,
        verifyStatus: 1,
      });

      if (result.success) {
        toast.success("Payment verified successfully");
        onActionComplete?.("approved", lead);
        onClose();
      } else {
        toast.error(result.error || result.message || "Failed to verify payment");
      }
    } catch (err) {
      toast.error("An unexpected error occurred");
    } finally {
      setLoading(null);
    }
  };

  const handleRefund = () => {
    onClose();
    if (onRefundPayment) {
      onRefundPayment(lead);
    }
  };

  const handleReject = () => {
    if (!isRejecting) {
      setIsRejecting(true);
      return;
    }

    if (!remarks.trim()) {
      toast.error("Please provide a reason for rejection");
      return;
    }

    setShowRejectConfirm(true);
  };

  const executeReject = async () => {
    setShowRejectConfirm(false);
    setLoading("reject");
    try {
      const result = await VerifyOrRejectPayment({
        paymentId: lead.PaymentId,
        verifyStatus: 2,
        remarks: remarks.trim(),
      });

      if (result.success) {
        toast.success("Payment rejected");
        onActionComplete?.("rejected", lead);
        onClose();
      } else {
        toast.error(result.error || result.message || "Failed to reject payment");
      }
    } catch (err) {
      toast.error("An unexpected error occurred");
    } finally {
      setLoading(null);
    }
  };



  return (
    <>
      <Transition appear show={isOpen} as={Fragment}>
        <Dialog as="div" className="relative z-[200]" onClose={onClose}>
          {/* Backdrop */}
          <TransitionChild
            as={Fragment}
            enter="ease-out duration-200"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-150"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" />
          </TransitionChild>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <TransitionChild
                as={Fragment}
                enter="ease-out duration-200"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-150"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <DialogPanel className="w-full max-w-lg bg-white dark:bg-gray-900 rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-700 overflow-hidden">
                  {/* Header */}
                  <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800">
                    <div className="flex items-center gap-3">
                      <div className={clsx(
                        "w-9 h-9 rounded-full flex items-center justify-center",
                        isUPI ? "bg-blue-100 dark:bg-blue-900/30" : "bg-green-100 dark:bg-green-900/30"
                      )}>
                        {isUPI
                          ? <CreditCardIcon className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                          : <BanknotesIcon className="w-5 h-5 text-green-600 dark:text-green-400" />
                        }
                      </div>
                      <div>
                        <DialogTitle className="text-sm font-semibold text-gray-900 dark:text-white">
                          {isVerified ? "Payment Details (Approved)" : isRejected ? "Payment Details (Rejected)" : isRefunded ? "Payment Details (Refunded)" : "Verify Payment"}
                        </DialogTitle>
                        <div className="flex flex-col gap-0.5 mt-0.5">
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            Payment : <span className="text-primary-600 dark:text-primary-400 font-bold">₹{Number(lead.PaymentAmount || 0).toLocaleString("en-IN")}</span> ({lead.PaymentMethod})
                          </p>
                        </div>
                      </div>
                    </div>
                    <Button
                      variant="flat"
                      isIcon
                      onClick={onClose}
                      className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                    >
                      <XMarkIcon className="w-5 h-5" />
                    </Button>
                  </div>

                  {/* Body */}
                  <div className="p-5 space-y-5">

                    {/* Attachment Section — UPI only */}
                    {isUPI && (
                      <div className="space-y-3">
                        {attachmentUrl ? (
                          <div className="rounded-xl overflow-hidden border border-gray-200 dark:border-gray-700 bg-gray-100 dark:bg-gray-800">
                            {isPdf ? (
                              <div className="flex flex-col items-center justify-center py-10 gap-3">
                                <DocumentIcon className="w-12 h-12 text-primary-600 dark:text-primary-400" />
                                <p className="text-sm font-semibold text-gray-700 dark:text-gray-200 truncate max-w-[250px]">{currentAttachment?.FileName}</p>
                                <a
                                  href={attachmentUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-sm text-primary-600 dark:text-primary-400 hover:underline font-medium flex items-center gap-1"
                                >
                                  <ArrowTopRightOnSquareIcon className="w-4 h-4" />
                                  Open PDF
                                </a>
                              </div>
                            ) : imageError ? (
                              <div className="flex flex-col items-center justify-center py-10 gap-3">
                                <DocumentIcon className="w-12 h-12 text-gray-400" />
                                <p className="text-sm text-gray-500 dark:text-gray-400">Unable to preview attachment</p>
                                <a
                                  href={attachmentUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-sm text-primary-600 dark:text-primary-400 hover:underline font-medium"
                                >
                                  Open Attachment
                                </a>
                              </div>
                            ) : (
                              <img
                                src={attachmentUrl}
                                alt="Payment attachment"
                                className="w-full max-h-72 object-contain mx-auto"
                                onError={() => setImageError(true)}
                              />
                            )}
                          </div>
                        ) : (
                          <div className="rounded-xl border border-dashed border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-800 flex flex-col items-center justify-center py-10 gap-2">
                            <DocumentIcon className="w-10 h-10 text-gray-300 dark:text-gray-600" />
                            <p className="text-sm text-gray-500 dark:text-gray-400">No attachment uploaded</p>
                          </div>
                        )}

                        {/* Multiple attachments thumbnails */}
                        {attachments.length > 1 && (
                          <div className="flex gap-2 overflow-x-auto pb-1 justify-center">
                            {attachments.map((att, idx) => {
                              const isAttPdf = att.FileType?.toLowerCase() === 'application/pdf' || att.FilePath?.toLowerCase().endsWith('.pdf');
                              const isSelected = idx === selectedAttachmentIndex;
                              return (
                                <button
                                  key={att.AttachmentId || idx}
                                  type="button"
                                  onClick={() => {
                                    setSelectedAttachmentIndex(idx);
                                    setImageError(false);
                                  }}
                                  className={clsx(
                                    "w-12 h-12 rounded-lg border-2 overflow-hidden flex-shrink-0 flex items-center justify-center bg-gray-50 dark:bg-gray-800 transition-all",
                                    isSelected ? "border-primary-500 scale-105" : "border-gray-200 dark:border-gray-700 hover:border-gray-350"
                                  )}
                                >
                                  {isAttPdf ? (
                                    <DocumentIcon className="w-6 h-6 text-gray-500" />
                                  ) : (
                                    <img src={getAttachmentUrl(att.FilePath)} alt="" className="w-full h-full object-cover" />
                                  )}
                                </button>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Cash — no attachment note */}
                    {!isUPI && (
                      <div className="rounded-xl bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 px-4 py-3 flex items-start gap-3">
                        <BanknotesIcon className="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="text-sm font-medium text-amber-800 dark:text-amber-300">Cash Payment</p>
                          <p className="text-xs text-amber-700 dark:text-amber-400 mt-0.5">
                            No digital attachment is required for cash payments. Please verify by physical receipt.
                          </p>
                        </div>
                      </div>
                    )}

                    {/* Previous Rejection Remarks */}
                    {isRejected && lead.RemarksForRejection && (
                      <div className="rounded-xl bg-red-50 dark:bg-red-900/10 border border-red-200 dark:border-red-800/50 px-4 py-3">
                        <p className="text-sm font-semibold text-red-800 dark:text-red-300">Previous Rejection Reason</p>
                        <p className="text-xs text-red-700 dark:text-red-400 mt-1">{lead.RemarksForRejection}</p>
                      </div>
                    )}

                    {/* Rejection Remarks */}
                    {isRejecting && (
                      <div className="space-y-2 animate-in fade-in slide-in-from-top-2 duration-200">
                        <label htmlFor="remarks" className="block text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                          Rejection Remarks <span className="text-red-500">*</span>
                        </label>
                        <textarea
                          id="remarks"
                          rows={3}
                          className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 dark:bg-gray-800 dark:text-white outline-none transition-all resize-none"
                          placeholder="Enter the reason for rejecting this payment..."
                          value={remarks}
                          onChange={(e) => setRemarks(e.target.value)}
                          autoFocus
                        />
                      </div>
                    )}
                  </div>

                  {/* Footer Actions */}
                  <div className="flex items-center justify-between px-5 py-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800">
                    <div className="flex items-center">
                      {attachmentUrl && !isRejecting && (
                        <a
                          href={attachmentUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-1.5 text-xs font-semibold text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 transition-colors uppercase tracking-wider"
                        >
                          <ArrowTopRightOnSquareIcon className="w-4 h-4" />
                          Open full
                        </a>
                      )}
                    </div>

                    <div className="flex items-center gap-3">
                      {isRejecting && (
                        <Button
                          variant="outlined"
                          onClick={() => {
                            setIsRejecting(false);
                            setRemarks("");
                          }}
                          disabled={!!loading}
                          className="px-4"
                        >
                          Cancel
                        </Button>
                      )}

                      {/* Show Reject button if: 
                        1) We are actively confirming the rejection (isRejecting is true)
                        2) The payment is not currently rejected (i.e. it is approved or pending)
                      */}
                      {isAdmin && (isRejecting || (!isRejected && !isVerified) || isVerified) && !isRefunded && (
                        <Button
                          color="error"
                          variant="filled"
                          disabled={!!loading || !lead.ReadyForVerification}
                          onClick={handleReject}
                          className="px-4 flex items-center gap-2"
                        >
                          {loading === "reject" ? (
                            <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin inline-block" />
                          ) : (
                            <XCircleIcon className="w-4 h-4" />
                          )}
                          {isRejecting ? "Confirm Reject" : "Reject"}
                        </Button>
                      )}

                      {/* Show Approve button if:
                          1) We are not in the rejection comments flow
                          2) The payment is not currently approved (i.e. it is rejected or pending)
                      */}
                      {isAdmin && !isRejecting && (!isVerified || isRejected) && !isRefunded && (
                        <Button
                          color="success"
                          variant="filled"
                          disabled={!!loading || !lead.ReadyForVerification}
                          onClick={handleApprove}
                          className="px-4 flex items-center gap-2"
                        >
                          {loading === "approve" ? (
                            <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin inline-block" />
                          ) : (
                            <CheckCircleIcon className="w-4 h-4" />
                          )}
                          Approve
                        </Button>
                      )}

                      {/* Show Refund button if:
                          1) We are not in the rejection comments flow
                          2) The payment is currently approved
                      */}
                      {isAdmin && !isRejecting && isVerified && !isRefunded && (
                        <Button
                          color="warning"
                          variant="filled"
                          disabled={!!loading || !lead.ReadyForVerification}
                          onClick={handleRefund}
                          className="px-4 flex items-center gap-2"
                        >
                          <ArrowPathIcon className="w-4 h-4" />
                          Refund
                        </Button>
                      )}
                    </div>
                  </div>
                </DialogPanel>
              </TransitionChild>
            </div>
          </div>
        </Dialog>
      </Transition>

      <ConfirmModal
        show={showRejectConfirm}
        onClose={() => setShowRejectConfirm(false)}
        onOk={executeReject}
        confirmLoading={loading === "reject"}
        state="pending"
        messages={{
          pending: {
            title: "Confirm Rejection",
            description: "Are you sure you want to reject this payment? This action will mark the payment as rejected.",
            actionText: "Reject",
          }
        }}
        buttonColor="error"
      />
    </>
  );
};

export default PaymentVerifyModal;
